import { ApiClientError } from 'grabbitbot-legacy-ui'

interface ExchangeErrorBody {
  error?: unknown
  code?: unknown
  message?: unknown
}

/** Maps a wallet/transfer failure to a user-facing sentence. The backend
 * answers `409 {error: <legacy class name>, message, code}` (codes 101 unknown
 * coin, 111/112 quantity too low, 201 balance too low) or
 * `{error: "invalid_quantity", ...}`. */
export function describeTransferError(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.status === 401) return 'Your session has expired — please sign in again.'
    const body = (err.raw ?? {}) as ExchangeErrorBody
    const code = body.code
    const cls = typeof body.error === 'string' ? body.error : ''
    if (code === 201 || cls.includes('WalletBalanceException')) return "You don't have enough balance for this transaction."
    if (code === 101 || cls.includes('WalletInvalidCoinException')) return 'The given coin is invalid.'
    if (code === 'invalid_quantity' || cls.includes('WalletQuantityException') || code === 111 || code === 112) {
      return 'The given amount is invalid.'
    }
    if (err.status === 0) return 'Could not reach WnF Exchange — check your connection and try again.'
  }
  return 'An unknown error occurred.'
}

/** Best-effort message for a failed read. */
export function describeLoadError(err: unknown, what: string): string {
  if (err instanceof ApiClientError && err.status === 0) return `Could not reach WnF Exchange while loading ${what}.`
  return `Could not load ${what}.`
}

/** True for the swap quote/execute answer `404 {"error":"no_pair"}`. */
export function isNoPairError(err: unknown): boolean {
  return err instanceof ApiClientError && err.status === 404 && (err.raw as ExchangeErrorBody | null)?.error === 'no_pair'
}

/** Maps a swap failure (quote or execute) to a user-facing sentence. */
export function describeSwapError(err: unknown): string {
  if (isNoPairError(err)) return 'These two coins cannot be swapped directly: there is no trading pair between them.'
  if (err instanceof ApiClientError) {
    const body = (err.raw ?? {}) as ExchangeErrorBody
    const cls = typeof body.error === 'string' ? body.error : ''
    if (body.code === 111 || body.code === 112 || cls.includes('WalletQuantityException')) {
      return 'The amount is too small to swap for this pair — try a larger amount.'
    }
  }
  return describeTransferError(err)
}
